import { PaymentAnimationStates } from 'src/app/shared/enums/payment-animation-states';
import { PaymentsCommonMethodsService } from './payments-common-methods.service';
import { PaymentResponeStatus } from '../../enums/payment-respone-status.enum';
import { PaymentResponse } from '../../types/payment-response.interface';
import { RealPurchaseData } from '../../types/purchase-data.interface';
import { PurchaseSource } from '../../enums/purchase-source.enum';
import { AnalyticsService } from '../analytics/analytics.service';
import { PaymentsStateService } from './payments-state.service';
import { environment } from 'src/environments/environment';
import { InventoryService } from '../inventory.service';
import { map, switchMap, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NutakuGi2 } from '../../plugins/nutaku-gi2';

export interface NutakuPaymentResponse {
  nutakuId: string;
  isOk: boolean;
}

interface NutakuPaymentMessage {
  action?: string;
  isOk?: boolean;
  data?: {
    nutakuId?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class NutakuPaymentService {
  private _host: string = environment.apiUrl;
  constructor(
    private _paymentsCommonMethodsService: PaymentsCommonMethodsService,
    private _paymentsStateService: PaymentsStateService,
    private _analyticsService: AnalyticsService,
    private _inventoryService: InventoryService,
    private _http: HttpClient,
  ) {}

  purchase(data: RealPurchaseData): Observable<boolean> {
    if (Capacitor.isNativePlatform()) {
      return this._nativePurchase(data);
    }

    if ((window as any).GmnNutakuBridge?.payment) {
      this._paymentsStateService.paymentState = PaymentAnimationStates.progress;
      return from(
        (window as any).GmnNutakuBridge.payment(data) as Promise<NutakuPaymentResponse>,
      ).pipe(switchMap((res) => this._handlePaymentBridgeResponse(data, res)));
    }

    return new Observable<NutakuPaymentResponse>((observer) => {
      this._paymentsStateService.paymentState = PaymentAnimationStates.progress;

      const onPaymentMessage = (res: MessageEvent) => {
        let result: NutakuPaymentMessage;
        try {
          result =
            typeof res?.data === 'string'
              ? JSON.parse(res.data)
              : (res?.data as NutakuPaymentMessage);
        } catch {
          return;
        }

        if (result?.action === 'payment') {
          observer.next({
            nutakuId: result?.data?.nutakuId || '',
            isOk: result.isOk || false,
          });
          observer.complete();
        }
      };

      window.addEventListener('message', onPaymentMessage, false);
      window.parent.postMessage(
        JSON.stringify({
          action: 'payment',
          data,
        }),
        '*',
      );

      return () => {
        window.removeEventListener('message', onPaymentMessage, false);
      };
    }).pipe(switchMap((res) => this._handlePaymentBridgeResponse(data, res)));
  }

  private _handlePaymentBridgeResponse(
    data: RealPurchaseData,
    res: NutakuPaymentResponse,
  ): Observable<boolean> {
    if (res.isOk) {
      this._paymentsStateService.paymentState = null;
      return this._afterPurchase(data.source, res.nutakuId, data.chatId);
    } else {
      this._paymentsStateService.paymentState = PaymentAnimationStates.error;
      return of(false);
    }
  }

  private _nativePurchase(data: RealPurchaseData): Observable<boolean> {
    this._paymentsStateService.paymentState = PaymentAnimationStates.progress;
    const nutakuData: Partial<NonNullable<RealPurchaseData['nutakuData']>> =
      data.nutakuData || {};
    const fallbackNutakuImageUrl =
      'https://nutaku.getmynudes.com/assets/img/logo-gmn-for-nutaku.png';

    return from(
      NutakuGi2.createPayment({
        price: data.purchase.nutakuCost,
        skuId: data.purchase.productId,
        name:
          nutakuData.nutakuName ||
          data.purchase.nutakuName ||
          data.purchase.productId,
        imgUrl:
          nutakuData.nutakuImageUrl ||
          data.purchase.nutakuImageUrl ||
          fallbackNutakuImageUrl,
        description:
          nutakuData.nutakuDescription ||
          data.purchase.nutakuDescription ||
          '',
        message:
          nutakuData.nutakuDescription ||
          data.purchase.nutakuDescription ||
          data.purchase.productId,
      }),
    ).pipe(
      switchMap((res) => {
        if (res.status === 'success' && res.paymentId) {
          this._paymentsStateService.paymentState = null;
          return this._afterPurchase(data.source, res.paymentId, data.chatId);
        }

        if (res.status !== 'cancel') {
          console.warn('Nutaku Android GI2 payment failed', res.error || res);
        }

        this._paymentsStateService.paymentState = PaymentAnimationStates.error;
        return of(false);
      }),
    );
  }

  private _afterPurchase(
    source: PurchaseSource,
    nutakuId: string,
    chatId = '',
  ): Observable<boolean> {
    return this._nutakuBuyRequest(source, nutakuId, chatId);
  }

  private _nutakuBuyRequest(
    source: PurchaseSource,
    nutakuId: string,
    chatId = '',
  ): Observable<boolean> {
    const requestUrl = `${this._host}/api/Shop/nutaku-buy`;

    return this._http
      .post<PaymentResponse>(requestUrl, {
        nutakuId,
        source,
        chatId,
      })
      .pipe(map((res) => this._nutakuBuyHandler(res, nutakuId)));
  }

  private _nutakuBuyHandler(res: PaymentResponse, nutakuId: string): boolean {
    const idForAnalytic = nutakuId;
    if (
      res.payment.status === PaymentResponeStatus.error ||
      res.payment.status === PaymentResponeStatus.cancelled
    ) {
      this._paymentsStateService.paymentState = PaymentAnimationStates.error;
      this._analyticsService.paymentError(
        res?.payment.platform,
        idForAnalytic,
        res?.payment?.productId,
      );

      return false;
    } else if (res.payment.status === PaymentResponeStatus.success) {
      this._paymentsStateService.paymentState = PaymentAnimationStates.success;
      if (!res.isRepeatRequest) {
        this._analyticsService.payment(
          res?.payment.platform,
          idForAnalytic,
          res.payment.cost,
          res?.payment?.productId,
          res?.payment?.lotType,
          res?.payment?.source,
          res?.payment?.meta,
          res?.payment?.offerType,
          res?.payment?.purchaseInfo,
          res?.payment?.status,
          res?.payment?.paymentNumber,
        );
      }
      this._inventoryService.setInventory(res.inventory);
      return true;
    } else if (res.payment.status === PaymentResponeStatus.issued) {
      // Retry retry(2),
    }

    return false;
  }

  /*
   * Using from WebPayments Service for APK Nutaku handling
   */
  public handleNutakuPurchase(res: PaymentResponse): Observable<boolean> {
    if (res.payment.status === PaymentResponeStatus.error) {
      return of(false);
    } else {
      return from(
        this._paymentsCommonMethodsService.showConfirmationModal(
          res.nutakuAmount,
        ),
      ).pipe(
        switchMap((isSubmited: { data?: boolean }) => {
          if (isSubmited.data) {
            return this.nutakuConfirmPayment(res.payment.nutakuPaymentId).pipe(
              map((confirmNutakuRes) => {
                switch (confirmNutakuRes.payment.status) {
                  case PaymentResponeStatus.insufficientFunds:
                    this._paymentsCommonMethodsService.showNoFundsModal();
                    return false;
                  case PaymentResponeStatus.success:
                  case PaymentResponeStatus.issued:
                  case PaymentResponeStatus.completed:
                    this._paymentsCommonMethodsService.checkTransaction();
                    return true;
                  default:
                    return false;
                }
              }),
            );
          } else {
            return of(false);
          }
        }),
      );
    }
  }

  nutakuConfirmPayment(nutakuId: string): Observable<PaymentResponse> {
    const requestUrl = `${this._host}/api/Shop/approve-nutaku-buy`;
    return this._http
      .post<PaymentResponse>(requestUrl, { nutakuId })
      .pipe(take(1));
  }
}
